import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Landing2Layout from './layout/Landing2Layout';
import PageHero2 from './components/PageHero2';
import Sym from './components/Sym';
import CTABand2 from './sections2/CTABand2';
import { PLANS, formatMXN } from './data/plans';

/**
 * Página Precios: calculadora (input + slider) que sugiere el tier, los 7 planes
 * reales + tarjeta custom para despachos. Números desde data/plans.js (sin drift
 * con billing). El «Más elegido» y el trial del plan inicial vienen del módulo.
 */
const NOTES = {
  'tier-20': 'Incluye 15 días de prueba',
  'tier-50': 'Para equipos que crecen',
  'tier-200': 'El favorito de las medianas',
  'tier-500': 'Nómina de varias áreas',
  'tier-2500': 'Grandes operaciones',
  'tier-5000': 'Corporativos',
  'tier-unlimited': 'Sin tope de colaboradores',
};

const planForCount = (n) => PLANS.find((p) => p.max === null || n <= p.max) || PLANS[PLANS.length - 1];

const Precios2 = () => {
  const navigate = useNavigate();
  const [emp, setEmp] = useState(20);
  const matched = planForCount(emp || 1);
  const onEmp = (e) => {
    const v = parseInt(e.target.value, 10);
    setEmp(Number.isNaN(v) ? 0 : Math.max(0, v));
  };

  return (
    <Landing2Layout title="Precios — PaySlip">
      <PageHero2
        eyebrow="Precios"
        title="Un precio fijo,"
        accent="según tu tamaño"
        subtitle="Sin costo por colaborador ni sorpresas. Todos los precios en pesos, con IVA incluido. Crece de plan cuando lo necesites."
        maxWidth={600}
      />

      {/* Calculadora */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: 'clamp(28px,4vw,44px) clamp(20px,5vw,40px) 0' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 24, padding: 'clamp(24px,3.5vw,40px)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ font: "700 .78rem 'IBM Plex Mono'", letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--label)' }}>Calcula tu plan</div>
              <h2 style={{ font: "600 clamp(1.5rem,3vw,2.1rem) 'Source Serif 4'", color: 'var(--heading)', margin: '6px 0 0' }}>¿Cuántos colaboradores tienes?</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="number" min="1" value={emp || ''} onChange={onEmp}
                style={{ width: 120, background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 12, padding: '14px 16px', color: 'var(--heading)', font: "700 1.3rem 'Hanken Grotesk'", textAlign: 'right', outline: 'none' }} />
              <span style={{ font: "500 1rem 'Hanken Grotesk'", color: 'var(--muted-2)' }}>colaboradores</span>
            </div>
          </div>
          <input type="range" min="1" max="1000" value={Math.min(emp || 1, 1000)} onChange={onEmp} style={{ width: '100%', margin: '28px 0 24px' }} />
          <div data-dark style={{ background: '#0A1326', border: '1px solid rgba(111,160,255,.25)', borderRadius: 18, padding: 24, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ font: "600 .82rem 'Hanken Grotesk'", color: '#8A97B2', marginBottom: 4 }}>Te toca el plan</div>
              <div style={{ font: "600 clamp(1.4rem,3vw,2rem) 'Source Serif 4'", color: '#fff' }}>{matched.rango} colaboradores</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'flex-end' }}>
                <span style={{ font: "600 clamp(2rem,4.5vw,2.9rem) 'Source Serif 4'", color: '#fff' }}>${formatMXN(matched.price)}</span>
                <span style={{ font: "500 1rem 'Hanken Grotesk'", color: '#8A97B2' }}>/mes</span>
              </div>
              <div style={{ font: "500 .82rem 'Hanken Grotesk'", color: '#6C7A96', marginTop: 2 }}>IVA incluido · MXN</div>
            </div>
          </div>
          <button type="button" onClick={() => navigate('/register')}
            style={{ width: '100%', marginTop: 18, padding: 16, font: "700 1.05rem 'IBM Plex Mono'", color: '#fff', background: '#2F6BFF', border: 'none', borderRadius: 13, cursor: 'pointer', boxShadow: '0 12px 30px -12px rgba(47,107,255,.9)', transition: 'transform .18s' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
            Comienza con el plan {matched.rango}
          </button>
        </div>
      </section>

      {/* Grid de planes */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(36px,5vw,60px) clamp(20px,5vw,40px) clamp(24px,4vw,40px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
          {PLANS.map((p) => {
            const isMatched = p.slug === matched.slug;
            const tuPlan = isMatched && !p.popular;
            return (
              <div key={p.slug} style={{ position: 'relative', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: 26, display: 'flex', flexDirection: 'column' }}>
                {p.popular && (
                  <>
                    <span style={{ position: 'absolute', inset: -1.5, border: '2px solid #2F6BFF', borderRadius: 20, pointerEvents: 'none', boxShadow: '0 0 34px -6px rgba(47,107,255,.6)' }} />
                    <span style={{ position: 'absolute', top: -13, left: 24, background: '#2F6BFF', color: '#fff', font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.04em', padding: '5px 12px', borderRadius: 999 }}>Más elegido</span>
                  </>
                )}
                {tuPlan && (
                  <>
                    <span style={{ position: 'absolute', inset: -1.5, border: '1.5px solid rgba(111,160,255,.55)', borderRadius: 20, pointerEvents: 'none' }} />
                    <span style={{ position: 'absolute', top: -13, left: 24, background: '#12203C', border: '1px solid rgba(111,160,255,.6)', color: '#8FB4FF', font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.04em', padding: '5px 12px', borderRadius: 999 }}>Tu plan</span>
                  </>
                )}
                <div style={{ font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--label)' }}>Colaboradores</div>
                <div style={{ font: "600 1.5rem 'Source Serif 4'", color: 'var(--heading)', margin: '4px 0 16px' }}>{p.rango}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ font: "600 2.3rem 'Source Serif 4'", color: 'var(--heading)' }}>${formatMXN(p.price)}</span>
                  <span style={{ font: "500 .95rem 'Hanken Grotesk'", color: 'var(--muted-2)' }}>/mes</span>
                </div>
                <div style={{ font: "500 .82rem 'Hanken Grotesk'", color: 'var(--faint)', margin: '6px 0 22px', flex: 1 }}>{NOTES[p.slug]}</div>
                <button type="button" onClick={() => navigate('/register')}
                  style={p.popular
                    ? { width: '100%', padding: 13, font: "700 .98rem 'IBM Plex Mono'", color: '#0E1A30', background: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer' }
                    : { width: '100%', padding: 13, font: "700 .98rem 'IBM Plex Mono'", color: 'var(--heading)', background: 'transparent', border: '1.5px solid var(--border-strong)', borderRadius: 12, cursor: 'pointer' }}>
                  {p.popular ? 'Comenzar' : 'Elegir plan'}
                </button>
              </div>
            );
          })}
          {/* Custom / despachos */}
          <div data-dark style={{ position: 'relative', background: 'linear-gradient(160deg,#0E1A32,#00173D)', border: '1px solid rgba(111,160,255,.2)', borderRadius: 20, padding: 26, display: 'flex', flexDirection: 'column' }}>
            <div style={{ font: "700 .72rem 'IBM Plex Mono'", letterSpacing: '.1em', textTransform: 'uppercase', color: '#6FA0FF' }}>Plan a la medida</div>
            <div style={{ font: "600 1.35rem/1.2 'Source Serif 4'", color: '#fff', margin: '10px 0 12px' }}>¿Un despacho con varias empresas?</div>
            <p style={{ font: "400 .95rem/1.5 'Hanken Grotesk'", color: '#AEBAD2', margin: '0 0 22px', flex: 1 }}>Diseñamos un plan a la medida con SLA dedicado y un contacto asignado.</p>
            <a onClick={() => navigate('/contact')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: "700 .98rem 'Hanken Grotesk'", color: '#fff', cursor: 'pointer' }}>
              Hablar con ventas<Sym name="arrow_forward" size={20} />
            </a>
          </div>
        </div>
        <p style={{ textAlign: 'center', font: "500 .9rem 'Hanken Grotesk'", color: 'var(--faint)', margin: '28px 0 0' }}>
          Todos los precios en MXN con IVA incluido. El plan 1–20 incluye 15 días de prueba gratis.
        </p>
      </section>

      <CTABand2
        title="Empieza gratis, decide después"
        subtitle="El plan inicial incluye 15 días de prueba, sin plazos forzosos."
      />
    </Landing2Layout>
  );
};

export default Precios2;
